import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';

import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { catchError, map, share, take, takeUntil, tap, withLatestFrom } from 'rxjs/operators';

import { SearchService, UserStoreService } from '@mediahaven/shared';
import {
    ActionGroup,
    ActionItem,
    ApiFragment,
    BaseContainer,
    DialogConfig,
    DialogRef,
    EllipsisMultiActionItem,
    JobsDataService,
    JobStatus,
    Metadata
} from '@stylehaven/components';
import { RecordErrors } from './record-error-dialog.entity';

@Component({
    selector: 'mhv-record-error-dialog',
    templateUrl: './record-error-dialog.component.html',
    styleUrls: ['./record-error-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class RecordErrorDialogComponent extends BaseContainer implements OnInit {
    private readonly records: ApiFragment[];
    public recordErrorPairs: RecordErrors[];
    public hasErrors: boolean = false;

    public mailToServices$: Observable<string[]>;
    public adminEmail: string;
    public detailUrl$: Observable<string[]>;

    public actionItems$: Observable<ActionGroup[]>;

    constructor(
        public config: DialogConfig,
        public dialog: DialogRef<void>,
        private readonly JobsDataService: JobsDataService,
        private readonly userStoreService: UserStoreService,
        private readonly searchService: SearchService
    ) {
        super();
        this.setLoading(true);
        this.records = this.config.data?.recordsData.map((metadata: Metadata) => new ApiFragment(metadata)) ?? [];
        this.adminEmail = this.config.data?.adminEmail;
        this.detailUrl$ = this.config.data?.detailUrl$;

        if (this.records.length === 0) {
            console.error('RecordErrorDialogComponent:: No records provided in dialog-config');
            this.dialog.close();
        }
    }

    ngOnInit(): void {
        this.initRecordErrorsStream();
        this.initMailToServicesStream();

        this.initActionItemStream();
    }

    private initRecordErrorsStream(): void {
        forkJoin(this.records.map(record => this.mapToRecordErrors(record)))
            .pipe(
                map(recordErrors => recordErrors.flat()),
                take(1),
                tap(() => this.setLoading(false)),
                takeUntil(this.destroy$)
            )
            .subscribe(recordErrorPairs => {
                this.recordErrorPairs = recordErrorPairs;
                this.hasErrors = recordErrorPairs.some(pair => pair.errors.length > 0);
            });
    }

    private mapToRecordErrors(record: ApiFragment): Observable<RecordErrors[]> {
        const staticErrors: string[] = this.getStalledError(record);
        const errors$: Observable<string[]> = this.getRecordFailedJobErrors(record).pipe(
            take(1),
            share(),
            map(jobErrors => [...staticErrors, ...jobErrors])
        );

        const jobErrors$: Observable<RecordErrors> = errors$.pipe(
            map(errors => ({
                recordTitle: record.getTitle(),
                errors
            }))
        );

        const rejectionErrors$: Observable<RecordErrors[]> = this.getRejectionErrors(record);

        return combineLatest([jobErrors$, rejectionErrors$]).pipe(
            map(([jobErrors, rejectionErrors]) => [jobErrors, ...rejectionErrors])
        );
    }

    private getStalledError(record: ApiFragment): string[] {
        if (record.hasIncompleteBrowse()) {
            return [this.translateService.translate('items.record-errors-dialog.errors.stalled')];
        }
        return [];
    }

    private getRejectionErrors(record: ApiFragment): Observable<RecordErrors[]> {
        return this.searchService.getRejections(record.getRecordId()).pipe(
            map(rejectionLists =>
                rejectionLists.map(rejectionList => ({
                    recordTitle: rejectionList.Breadcrumb,
                    errors: rejectionList.Rejections.map(rejection => rejection.Description ?? rejection.Motivation)
                }))
            )
        );
    }

    private getRecordFailedJobErrors(record: ApiFragment): Observable<string[]> {
        if (!record.hasFailedJobs()) {
            return of([]);
        }

        // Show actual error only to system users
        return this.JobsDataService.getAllRecordJobs(record.getRecordId()).pipe(
            map(jobs => jobs.filter(job => job.Status === JobStatus.FAILED)),
            withLatestFrom(this.userStoreService.getCurrentUser()),
            map(([jobs, user]) => jobs.map(job => (user.IsSystem ? job.Error : this.noInternalRightsErrorMessage))),
            catchError(() => of([this.noInternalRightsErrorMessage]))
        );
    }

    private initMailToServicesStream(): void {
        const recordTitle: { title: string }[] = this.records.map((record: ApiFragment) => {
            return { title: record.getTitle() };
        });

        this.mailToServices$ = this.detailUrl$.pipe(
            map((detail: string[]) =>
                recordTitle.map((info, i) => {
                    const subject = `${this.translateService.translate(
                        'items.record-errors-dialog.mail-to-services-subject',
                        { recordTitle: info.title }
                    )}`;

                    return `mailto:${this.adminEmail}?subject=Issue report: ${subject}&body=${detail[i]}`;
                })
            )
        );
    }

    private initActionItemStream(): void {
        const actionsWithoutEllipsis$: Observable<ActionItem[]> = this.mailToServices$.pipe(
            map((mail: string[]) =>
                mail.map((finalMail: string) => ({
                    label: this.translateService.translate('actions.contact-admin'),
                    eventListener: () => this.mailToServicesAction(finalMail)
                }))
            )
        );

        this.actionItems$ = actionsWithoutEllipsis$.pipe(
            map((initialActions: ActionItem[]) =>
                initialActions.map((action: ActionItem) => EllipsisMultiActionItem([action]))
            ),
            map((ellipsisActions: ActionItem[]) =>
                ellipsisActions.flatMap((finalAction: ActionItem) => [{ actions: [finalAction] }])
            )
        );
    }

    private mailToServicesAction(mail: string): void {
        window.open(mail, '_blank', 'noopener, noreferrer');
    }

    get noInternalRightsErrorMessage(): string {
        return this.translateService.translate('items.record-errors-dialog.errors.internal');
    }
}
